// Cached token management
let cachedToken: string | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 30000; // 30 seconds

/**
 * Get CSRF token with comprehensive fallback strategy and caching.
 *
 * Priority order:
 * 1. window.initialData.csrfToken (from Django template)
 * 2. csrftoken cookie (set by Django)
 * 3. /_allauth/browser/v1/config API call (fallback)
 *
 * @returns Promise resolving to CSRF token or null if unavailable
 */
export const getCsrfToken = async (): Promise<string | null> => {
  const now = Date.now();

  // Return cached token if still valid
  if (cachedToken && now - cacheTimestamp < CACHE_TTL) {
    return cachedToken;
  }

  // 1. Try window.initialData (from Django template)
  if (window.initialData?.csrfToken) {
    cachedToken = window.initialData.csrfToken;
    cacheTimestamp = now;
    return cachedToken;
  }

  // 2. Try cookie parsing
  const cookieToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken="))
    ?.split("=")[1];

  if (cookieToken) {
    cachedToken = cookieToken;
    cacheTimestamp = now;
    return cachedToken;
  }

  // 3. Fallback: API call to get fresh token
  try {
    const response = await fetch("/_allauth/browser/v1/config", {
      credentials: "include",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (response.ok) {
      const data = await response.json();

      if (data.csrf_token) {
        cachedToken = data.csrf_token;
        cacheTimestamp = now;
        return cachedToken;
      }
    }
  } catch (error) {
    console.warn("Failed to fetch CSRF token:", error);
  }

  return null;
};

/**
 * Synchronous version of getCsrfToken for cases where async isn't possible.
 * Only uses window.initialData and cookie parsing (no API call).
 *
 * @returns CSRF token or null if unavailable
 */
export const getCsrfTokenSync = (): string | null => {
  // Try cached token first (if still valid)
  const now = Date.now();
  if (cachedToken && now - cacheTimestamp < CACHE_TTL) {
    return cachedToken;
  }

  // Try window.initialData
  if (window.initialData?.csrfToken) {
    cachedToken = window.initialData.csrfToken;
    cacheTimestamp = now;
    return cachedToken;
  }

  // Try cookie parsing
  const cookieToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken="))
    ?.split("=")[1];

  if (cookieToken) {
    cachedToken = cookieToken;
    cacheTimestamp = now;
    return cachedToken;
  }

  return null;
};

/**
 * Clear the cached CSRF token.
 * Useful when token becomes invalid and a fresh one is needed.
 */
export const clearCsrfTokenCache = (): void => {
  cachedToken = null;
  cacheTimestamp = 0;
};

/**
 * Helper to add CSRF token to request headers.
 *
 * @param headers Existing headers object
 * @returns Headers object with CSRF token added (if available)
 */
export const addCsrfTokenToHeaders = async (
  headers: Record<string, string> = {},
): Promise<Record<string, string>> => {
  const csrfToken = await getCsrfToken();

  if (csrfToken) {
    return {
      ...headers,
      "X-CSRFToken": csrfToken,
    };
  }

  return headers;
};

/**
 * Synchronous version of addCsrfTokenToHeaders.
 *
 * @param headers Existing headers object
 * @returns Headers object with CSRF token added (if available)
 */
export const addCsrfTokenToHeadersSync = (
  headers: Record<string, string> = {},
): Record<string, string> => {
  const csrfToken = getCsrfTokenSync();

  if (csrfToken) {
    return {
      ...headers,
      "X-CSRFToken": csrfToken,
    };
  }

  return headers;
};
